import { AssertNeverError } from '@omnea-digital/toolbox'
import type { RecordAccessGrantDetail, RecordAccessHolder } from './useRecordGrants'
import type { RecordAccessGrantChange, RecordGrantConfiguration, RecordGrantSubject } from './useUpdateRecordAccessGrant'

export type DraftGrant = {
  subject: RecordGrantSubject
  configuration: RecordGrantConfiguration
  /** An archived user may only be revoked — the endpoint rejects a grant naming one. */
  canOnlyBeRemoved: boolean
}

/** Users, roles and groups are separate id spaces, so a subject is only identified by both. */
export const subjectKey = (subject: RecordGrantSubject): string => {
  switch (subject.kind) {
    case 'user':
      return `user-${subject.userId.toLowerCase()}`
    case 'role':
      return `role-${subject.roleId.toLowerCase()}`
    case 'group':
      return `group-${subject.groupId.toLowerCase()}`
    default:
      throw new AssertNeverError(subject)
  }
}

/** Encodes the fixed/custom split into one string, so it survives the round trip through a select. */
export const configurationKey = (configuration: RecordGrantConfiguration): string =>
  configuration.kind === 'fixed' ? `fixed:${configuration.level}` : `custom:${configuration.permissionSetId}`

export const grantSubjectOf = (holder: RecordAccessHolder): RecordGrantSubject => {
  switch (holder.subject.kind) {
    case 'user':
      return { kind: 'user', userId: holder.subject.user.id }
    case 'role':
      return { kind: 'role', roleId: holder.subject.role.id }
    case 'group':
      return { kind: 'group', groupId: holder.subject.group.id }
    default:
      throw new AssertNeverError(holder.subject)
  }
}

/** Deactivation revokes org and config access but leaves per-record grants, so archived users still list. */
export const isArchivedSubject = (holder: RecordAccessHolder): boolean =>
  holder.subject.kind === 'user' && holder.subject.user.isArchived

/** Only a configuration the grant endpoint accepts round-trips; `none` is the absence of one. */
export const grantConfigurationOf = (configuration: RecordAccessGrantDetail): RecordGrantConfiguration | null => {
  switch (configuration.kind) {
    case 'fixed':
      return { kind: 'fixed', level: configuration.level }
    case 'custom':
      return { kind: 'custom', permissionSetId: configuration.permissionSetId }
    case 'none':
      return null
    default:
      throw new AssertNeverError(configuration)
  }
}

/**
 * The changes that turn the access the dialog opened on into the pending draft.
 *
 * `held` is where the edit began, never the current server state: diffing against live state would
 * re-grant something another admin revoked while the dialog was open.
 *
 * Grants are one-per-subject, so a subject appears at most once — a re-configure is a single grant
 * rather than a revoke and a grant, and the endpoint rejects a batch naming one subject twice.
 */
export const recordGrantDiff = ({
  held,
  pending,
}: {
  held: readonly DraftGrant[]
  pending: readonly DraftGrant[]
}): RecordAccessGrantChange[] => {
  const baseline = new Map(held.map((grant) => [subjectKey(grant.subject), grant]))
  const draft = new Map(pending.map((grant) => [subjectKey(grant.subject), grant]))

  const changes: RecordAccessGrantChange[] = []
  for (const [key, grant] of baseline) {
    if (!draft.has(key)) changes.push({ op: 'revoke', subject: grant.subject })
  }
  for (const [key, grant] of draft) {
    const previous = baseline.get(key)
    if (previous != null && configurationKey(previous.configuration) === configurationKey(grant.configuration)) continue
    changes.push({ op: 'grant', subject: grant.subject, configuration: grant.configuration })
  }
  return changes
}

export const hasDuplicateSubject = (pending: readonly DraftGrant[]): boolean =>
  new Set(pending.map((grant) => subjectKey(grant.subject))).size < pending.length
